import keycloakService from '@/services/keycloakService';
import ToastService from '@/services/toast';
import axios from 'axios';

const createHttpClient = baseURL => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    async config => {
      if (config.skipAuth) return config;

      const token = await keycloakService.getToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    error => Promise.reject(error)
  );

  instance.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;

      if (axios.isCancel(error)) return Promise.reject(error);
      const isRetryable =
        error.message === 'Network Error' ||
        (error.response?.status >= 500 && error.response?.status <= 504);
      if (isRetryable && (!originalRequest._retryCount || originalRequest._retryCount < 2)) {
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
        const delay = originalRequest._retryCount * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return instance(originalRequest);
      }

      if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.skipAuth) {
        originalRequest._retry = true;
        const hasRefreshToken = !!keycloakService.getCustomToken()?.refresh_token;
        if (!hasRefreshToken && !keycloakService.isLoggedIn()) {
          keycloakService.logout();
          return Promise.reject(error);
        }
        try {
          const success = await keycloakService.refreshToken();
          if (success) {
            const newToken = await keycloakService.getToken();
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return instance(originalRequest);
          }
        } catch (refreshError) {
          keycloakService.logout();
          return Promise.reject(refreshError);
        }
      }

      if (error.response) {
        const { status } = error.response;
        if (status === 403 && !originalRequest.skipAuth) {
          ToastService.warning('Access Forbidden', 'You do not have permission.');
        } else if (status >= 500) {
          ToastService.error('Server Error', 'Something went wrong on the server.');
        }
      } else if (error.request) {
        ToastService.error('Network Error', 'Please check your internet connection.');
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const baseURL = process.env.VUE_APP_API_BASE_URL;
const httpClient = createHttpClient(baseURL);

export default httpClient;
